import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import type {
  Affiliate,
  ApiList,
  Balance,
  Conversion,
  ConversionStatus,
  ConversionType,
  DashboardKpis,
  DashboardSeries,
  LedgerEntry,
  LinkEvent,
  Offer,
  Payout,
  PayoutMethod,
  PayoutStatus,
  Period,
  TrackingLink,
} from './types';

const BASE = '/api/v2/affiliate';

// ─── Auth ──────────────────────────────────────────────────────────────
export interface RegisterPayload {
  email: string;
  password: string;
  telegramContact?: string;
  trafficSource?: string;
  applicationSource?: string;
  audienceSize?: string;
}
export interface RegisterResponse {
  id: string;
  email: string;
  status: 'pending';
  message: string;
}
export interface LoginPayload {
  email: string;
  password: string;
}
export interface LoginResponse {
  id: string;
  email: string;
  status: 'approved';
}

function useRegister() {
  return useMutation({
    mutationFn: (p: RegisterPayload) => api.post<RegisterResponse>(`${BASE}/auth/register`, p),
  });
}
function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: LoginPayload) => api.post<LoginResponse>(`${BASE}/auth/login`, p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['affiliate', 'me'] });
    },
  });
}
function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>(`${BASE}/auth/logout`),
    onSuccess: () => {
      qc.clear();
    },
  });
}
function useResetRequest() {
  return useMutation({
    mutationFn: (p: { email: string }) => api.post<{ ok: true }>(`${BASE}/auth/reset-password/request`, p),
  });
}
function useResetConfirm() {
  return useMutation({
    mutationFn: (p: { token: string; password: string }) =>
      api.post<{ ok: true }>(`${BASE}/auth/reset-password/confirm`, p),
  });
}

// ─── Me ────────────────────────────────────────────────────────────────
function useMe(enabled = true) {
  return useQuery<Affiliate>({
    queryKey: ['affiliate', 'me'],
    queryFn: () => api.get<Affiliate>(`${BASE}/me`),
    enabled,
    retry: false,
    meta: { silent: true },
    refetchOnWindowFocus: true,
    refetchInterval: 15_000,
  });
}

// ─── Dashboard ─────────────────────────────────────────────────────────
function useKpis(period: Period) {
  return useQuery<DashboardKpis>({
    queryKey: ['affiliate', 'kpis', period],
    queryFn: () => api.get<DashboardKpis>(`${BASE}/dashboard/kpis?period=${period}`),
  });
}
function useSeries(period: Period) {
  return useQuery<DashboardSeries>({
    queryKey: ['affiliate', 'series', period],
    queryFn: () => api.get<DashboardSeries>(`${BASE}/dashboard/series?period=${period}`),
  });
}

// ─── Offers ────────────────────────────────────────────────────────────
function useOffers() {
  return useQuery<ApiList<Offer>>({
    queryKey: ['affiliate', 'offers'],
    queryFn: () => api.get<ApiList<Offer>>(`${BASE}/offers`),
  });
}
function useOffer(slug: string | undefined) {
  return useQuery<Offer>({
    queryKey: ['affiliate', 'offer', slug],
    queryFn: () => api.get<Offer>(`${BASE}/offers/${slug}`),
    enabled: !!slug,
  });
}
function useSelectOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) =>
      api.post<{ active: boolean; activeOfferSlugs: string[] }>(`${BASE}/offers/${slug}/select`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['affiliate', 'me'] });
      qc.invalidateQueries({ queryKey: ['affiliate', 'offers'] });
    },
  });
}

// ─── Links ─────────────────────────────────────────────────────────────
export interface CreateLinkPayload {
  name: string;
  /** Cтавится в `ad_links.target_url` напрямую — fallback если бэк не резолвит. */
  targetUrl?: string;
  /** Если задан — бэк сам берёт `landing_url` из `affiliate_offer_packages` (приоритет). */
  offerSlug?: string;
  offerId?: number;
}
export interface UpdateLinkPayload {
  name?: string;
  status?: 'active' | 'disabled';
  /** Изменение URL цели (для починки старых ссылок без удаления). */
  targetUrl?: string;
}
function useLinks() {
  return useQuery<ApiList<TrackingLink>>({
    queryKey: ['affiliate', 'links'],
    queryFn: () => api.get<ApiList<TrackingLink>>(`${BASE}/links`),
  });
}
function useCreateLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateLinkPayload) => api.post<TrackingLink>(`${BASE}/links`, p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['affiliate', 'links'] }),
  });
}
function useUpdateLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: number; data: UpdateLinkPayload }) =>
      api.patch<TrackingLink>(`${BASE}/links/${args.id}`, args.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['affiliate', 'links'] }),
  });
}
function useDeleteLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ ok: true }>(`${BASE}/links/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['affiliate', 'links'] }),
  });
}
function useLinkEvents(id: string | undefined) {
  return useQuery<ApiList<LinkEvent>>({
    queryKey: ['affiliate', 'link-events', id],
    queryFn: () => api.get<ApiList<LinkEvent>>(`${BASE}/links/${id}/events`),
    enabled: !!id,
  });
}

// ─── Conversions ───────────────────────────────────────────────────────
export interface ConversionsQuery {
  status?: ConversionStatus;
  type?: ConversionType;
  period?: Period;
  page?: number;
  pageSize?: number;
}
function useConversions(q: ConversionsQuery = {}) {
  const qs = new URLSearchParams();
  Object.entries(q).forEach(([k, v]) => {
    if (v != null && v !== '') qs.set(k, String(v));
  });
  return useQuery<ApiList<Conversion>>({
    queryKey: ['affiliate', 'conversions', q],
    queryFn: () => api.get<ApiList<Conversion>>(`${BASE}/conversions?${qs.toString()}`),
    refetchOnWindowFocus: true,
    refetchInterval: 15_000,
  });
}
function useConversion(id: string | undefined) {
  return useQuery<Conversion>({
    queryKey: ['affiliate', 'conversion', id],
    queryFn: () => api.get<Conversion>(`${BASE}/conversions/${id}`),
    enabled: !!id,
  });
}

// ─── Balance ───────────────────────────────────────────────────────────
function useBalance() {
  return useQuery<Balance>({
    queryKey: ['affiliate', 'balance'],
    queryFn: () => api.get<Balance>(`${BASE}/balance`),
    refetchOnWindowFocus: true,
    refetchInterval: 15_000,
  });
}
export interface LedgerQuery {
  page?: number;
  pageSize?: number;
}
function useLedger(q: LedgerQuery = {}) {
  const qs = new URLSearchParams();
  Object.entries(q).forEach(([k, v]) => {
    if (v != null) qs.set(k, String(v));
  });
  return useQuery<ApiList<LedgerEntry>>({
    queryKey: ['affiliate', 'ledger', q],
    queryFn: () => api.get<ApiList<LedgerEntry>>(`${BASE}/balance/ledger?${qs.toString()}`),
  });
}

// ─── Payouts ───────────────────────────────────────────────────────────
export interface PayoutsQuery {
  status?: PayoutStatus;
  page?: number;
  pageSize?: number;
}
export interface CreatePayoutPayload {
  amountCents: number;
  method: PayoutMethod;
  address: string;
  /** Optional network / tag / memo — accepted by the backend payout schema. */
  network?: string;
}
function usePayouts(q: PayoutsQuery = {}) {
  const qs = new URLSearchParams();
  Object.entries(q).forEach(([k, v]) => {
    if (v != null && v !== '') qs.set(k, String(v));
  });
  return useQuery<ApiList<Payout>>({
    queryKey: ['affiliate', 'payouts', q],
    queryFn: () => api.get<ApiList<Payout>>(`${BASE}/payouts?${qs.toString()}`),
    refetchOnWindowFocus: true,
    refetchInterval: 15_000,
  });
}
function usePayout(id: string | undefined) {
  return useQuery<Payout>({
    queryKey: ['affiliate', 'payout', id],
    queryFn: () => api.get<Payout>(`${BASE}/payouts/${id}`),
    enabled: !!id,
  });
}
function useCreatePayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreatePayoutPayload) =>
      api.post<Payout>(`${BASE}/payouts`, p, {
        headers: { 'Idempotency-Key': cryptoUuid() },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['affiliate', 'payouts'] });
      qc.invalidateQueries({ queryKey: ['affiliate', 'balance'] });
    },
  });
}

function cryptoUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ─── Profile ───────────────────────────────────────────────────────────
export interface UpdateProfilePayload {
  displayName?: string;
  telegramContact?: string;
  paymentMethod?: PayoutMethod;
  paymentAddress?: string;
}
export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}
function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: UpdateProfilePayload) => api.patch<Affiliate>(`${BASE}/profile`, p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['affiliate', 'me'] }),
  });
}
function useChangePassword() {
  return useMutation({
    mutationFn: (p: ChangePasswordPayload) =>
      api.post<{ ok: true }>(`${BASE}/profile/change-password`, p),
  });
}

// ─── Activity ──────────────────────────────────────────────────────────
export type ActivityType =
  | 'conversion'
  | 'payout'
  | 'application_decided'
  | 'tier_changed'
  | (string & {});

export interface ActivityItem {
  id: string;
  type: ActivityType;
  title: string;
  body?: string | null;
  createdAt: string;
  refId?: string | number | null;
}

export interface ActivityListResponse {
  items: ActivityItem[];
  total: number;
}

function useActivity(opts: { limit?: number } = {}) {
  const limit = opts.limit;
  const qs = new URLSearchParams();
  if (limit != null) qs.set('limit', String(limit));
  const path = qs.toString() ? `${BASE}/activity?${qs.toString()}` : `${BASE}/activity`;
  return useQuery<ActivityListResponse>({
    queryKey: ['affiliate', 'activity', limit],
    queryFn: () => api.get<ActivityListResponse>(path),
    staleTime: 30_000,
  });
}

// ─── Dashboard MEGA (v2.3) ─────────────────────────────────────────────
export type AnalyticsPeriod = '7d' | '30d' | '90d';

export interface LinkAnalytics {
  linkId: number;
  period: AnalyticsPeriod;
  totals: { clicks: number; conversions: number; commissionCents: number };
  hourly: Array<{ hour: number; count: number }>;
  daily: Array<{ date: string; count: number }>;
  byGeo: Array<{ country: string; count: number }>;
  byDevice: Array<{ device: string; count: number }>;
  bySource: Array<{ source: string; count: number }>;
  funnel: Array<{ label: string; count: number }>;
}
function useLinkAnalytics(linkId: number | undefined, period: AnalyticsPeriod = '30d') {
  return useQuery<LinkAnalytics>({
    queryKey: ['affiliate', 'analytics', 'link', linkId, period],
    queryFn: () => api.get<LinkAnalytics>(`${BASE}/analytics/link/${linkId}?period=${period}`),
    enabled: !!linkId,
  });
}

export interface TierProgress {
  currentTier: string;
  nextTier: string | null;
  thresholds: Array<{ tier: string; thresholdCents: number }>;
  earnedCents30d: number;
  benefits: string[];
  progressPct: number;
}
function useTierProgress() {
  return useQuery<TierProgress>({
    queryKey: ['affiliate', 'tier-progress'],
    queryFn: () => api.get<TierProgress>(`${BASE}/tier-progress`),
  });
}

export interface PayoutForecast {
  totalHoldCents: number;
  totalAvailableCents: number;
  holdDays: number;
  releases: Array<{ date: string; amountCents: number }>;
}
function usePayoutForecast() {
  return useQuery<PayoutForecast>({
    queryKey: ['affiliate', 'payout-forecast'],
    queryFn: () => api.get<PayoutForecast>(`${BASE}/payout-forecast`),
  });
}

export interface LeaderboardItem {
  rank: number;
  alias: string;
  commissionCents: number;
  conversions: number;
  tier: string;
  isMe: boolean;
}
export interface LeaderboardResponse {
  items: LeaderboardItem[];
  total: number;
  myRank: number | null;
}
function useLeaderboard(period: '7d' | '30d' = '30d') {
  return useQuery<LeaderboardResponse>({
    queryKey: ['affiliate', 'leaderboard', period],
    queryFn: () => api.get<LeaderboardResponse>(`${BASE}/leaderboard?period=${period}`),
  });
}

export interface OnboardingStatus {
  pickedOffer: boolean;
  createdLink: boolean;
  firstClick: boolean;
  firstConversion: boolean;
}
function useOnboardingStatus() {
  return useQuery<OnboardingStatus>({
    queryKey: ['affiliate', 'onboarding-status'],
    queryFn: () => api.get<OnboardingStatus>(`${BASE}/onboarding-status`),
  });
}

export interface RealtimePulse {
  windowMinutes: number;
  clicksLastHour: number;
  clicksPerMin: number;
  recentTimestamps: string[];
}
function useRealtimePulse(windowMinutes = 60) {
  return useQuery<RealtimePulse>({
    queryKey: ['affiliate', 'realtime-pulse', windowMinutes],
    queryFn: () => api.get<RealtimePulse>(`${BASE}/realtime/pulse?windowMinutes=${windowMinutes}`),
    refetchInterval: 30_000,
  });
}

export interface LifetimeStats {
  totalEarnedCents: number;
  totalConversions: number;
  totalClicks: number;
  partnerSince: string;
}
function useLifetime() {
  return useQuery<LifetimeStats>({
    queryKey: ['affiliate', 'lifetime'],
    queryFn: () => api.get<LifetimeStats>(`${BASE}/lifetime`),
  });
}

export interface HeatmapResponse {
  cells: Array<{ date: string; commissionCents: number; conversions: number }>;
  maxCommission: number;
}
function useHeatmap(days = 90) {
  return useQuery<HeatmapResponse>({
    queryKey: ['affiliate', 'heatmap', days],
    queryFn: () => api.get<HeatmapResponse>(`${BASE}/dashboard/heatmap?days=${days}`),
  });
}

export interface AnomalyResponse {
  hasAnomaly: boolean;
  kind?: 'clicks_drop' | 'conv_drop' | 'fraud_flag';
  message?: string;
}
function useAnomaly() {
  return useQuery<AnomalyResponse>({
    queryKey: ['affiliate', 'anomaly'],
    queryFn: () => api.get<AnomalyResponse>(`${BASE}/dashboard/anomaly`),
  });
}

export interface TodayVsYesterday {
  today: { clicks: number; leads: number; conversions: number; commissionCents: number };
  yesterday: { clicks: number; leads: number; conversions: number; commissionCents: number };
  deltaPct: {
    clicks: number | null;
    leads: number | null;
    conversions: number | null;
    commissionCents: number | null;
  };
}
function useTodayVsYesterday() {
  return useQuery<TodayVsYesterday>({
    queryKey: ['affiliate', 'today-vs-yesterday'],
    queryFn: () => api.get<TodayVsYesterday>(`${BASE}/dashboard/today-vs-yesterday`),
    refetchInterval: 60_000,
  });
}

export interface SmartSuggestion {
  id: string;
  kind: string;
  title: string;
  body?: string;
  ctaLabel?: string;
  ctaHref?: string;
}
function useSmartSuggestions() {
  return useQuery<{ items: SmartSuggestion[] }>({
    queryKey: ['affiliate', 'smart-suggestions'],
    queryFn: () => api.get<{ items: SmartSuggestion[] }>(`${BASE}/dashboard/smart-suggestions`),
  });
}

export interface DeclinedBreakdown {
  totalDeclined: number;
  totalApproved: number;
  byReason: Array<{ reason: string; count: number; share: number }>;
}
function useDeclinedBreakdown(period: '7d' | '30d' | '90d' = '30d') {
  return useQuery<DeclinedBreakdown>({
    queryKey: ['affiliate', 'declined-breakdown', period],
    queryFn: () => api.get<DeclinedBreakdown>(`${BASE}/dashboard/declined-breakdown?period=${period}`),
  });
}

export interface TopLoserLink {
  linkId: number;
  name: string;
  code: string;
  clicks: number;
  conversions: number;
  epcCents: number;
}
function useTopLoserLink(period: '7d' | '30d' | '90d' = '30d') {
  return useQuery<TopLoserLink | null>({
    queryKey: ['affiliate', 'top-loser-link', period],
    queryFn: () => api.get<TopLoserLink | null>(`${BASE}/dashboard/top-loser-link?period=${period}`),
  });
}

export interface QualityScore {
  score: number;
  band: 'good' | 'medium' | 'risk';
  factors: Array<{ label: string; score: number }>;
}
function useQualityScore() {
  return useQuery<QualityScore>({
    queryKey: ['affiliate', 'quality-score'],
    queryFn: () => api.get<QualityScore>(`${BASE}/dashboard/quality-score`),
  });
}

export interface ChangelogItem {
  id: number;
  kind: string | null;
  title: string;
  body: string | null;
  createdAt: string;
}
function useChangelog(limit = 10) {
  return useQuery<{ items: ChangelogItem[] }>({
    queryKey: ['affiliate', 'changelog', limit],
    queryFn: () => api.get<{ items: ChangelogItem[] }>(`${BASE}/dashboard/changelog?limit=${limit}`),
  });
}

export interface MinPayoutProgress {
  currentBalanceCents: number;
  minPayoutCents: number;
  progressPct: number;
}
function useMinPayoutProgress() {
  return useQuery<MinPayoutProgress>({
    queryKey: ['affiliate', 'min-payout-progress'],
    queryFn: () => api.get<MinPayoutProgress>(`${BASE}/dashboard/min-payout-progress`),
  });
}

export interface StreakInfo {
  current: number;
  longest: number;
  lastConversionAt: string | null;
}
function useStreak() {
  return useQuery<StreakInfo>({
    queryKey: ['affiliate', 'streak'],
    queryFn: () => api.get<StreakInfo>(`${BASE}/dashboard/streak`),
  });
}

export interface MtdProgress {
  earnedCents: number;
  prevMonthCents: number;
  deltaPct: number | null;
  daysElapsed: number;
  daysInMonth: number;
}
function useMtdProgress() {
  return useQuery<MtdProgress>({
    queryKey: ['affiliate', 'mtd-progress'],
    queryFn: () => api.get<MtdProgress>(`${BASE}/dashboard/mtd-progress`),
  });
}

export interface CohortComparison {
  myCohortMonth: string | null;
  avgCommissionInCohort: number;
  myCommission: number;
  myRankInCohort: number | null;
  cohortSize: number;
}
function useCohortComparison() {
  return useQuery<CohortComparison>({
    queryKey: ['affiliate', 'cohort-comparison'],
    queryFn: () => api.get<CohortComparison>(`${BASE}/dashboard/cohort-comparison`),
  });
}

export interface AutoPayoutPayload {
  enabled: boolean;
  thresholdCents?: number | null;
}
function useUpdateAutoPayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: AutoPayoutPayload) =>
      api.patch<{ enabled: boolean; thresholdCents: number | null }>(
        `${BASE}/profile/auto-payout`,
        p,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['affiliate', 'balance'] });
      qc.invalidateQueries({ queryKey: ['affiliate', 'me'] });
    },
  });
}

export const partnerApi = {
  auth: {
    useRegister,
    useLogin,
    useLogout,
    useResetRequest,
    useResetConfirm,
  },
  me: { useGet: useMe },
  dashboard: { useKpis, useSeries },
  offers: { useList: useOffers, useGet: useOffer, useSelect: useSelectOffer },
  links: {
    useList: useLinks,
    useCreate: useCreateLink,
    useUpdate: useUpdateLink,
    useDelete: useDeleteLink,
    useEvents: useLinkEvents,
  },
  conversions: { useList: useConversions, useGet: useConversion },
  balance: { useGet: useBalance, useLedger },
  payouts: { useList: usePayouts, useGet: usePayout, useCreate: useCreatePayout },
  profile: { useUpdate: useUpdateProfile, useChangePassword },
  activity: { useList: useActivity },
  analytics: { useLink: useLinkAnalytics },
  tier: { useProgress: useTierProgress },
  forecast: { useGet: usePayoutForecast },
  leaderboard: { useGet: useLeaderboard },
  onboarding: { useStatus: useOnboardingStatus },
  realtime: { usePulse: useRealtimePulse },
  lifetime: { useGet: useLifetime },
  heatmap: { useGet: useHeatmap },
  anomaly: { useGet: useAnomaly },
  todayVsYesterday: { useGet: useTodayVsYesterday },
  smartSuggestions: { useList: useSmartSuggestions },
  declinedBreakdown: { useGet: useDeclinedBreakdown },
  topLoserLink: { useGet: useTopLoserLink },
  qualityScore: { useGet: useQualityScore },
  changelog: { useList: useChangelog },
  minPayoutProgress: { useGet: useMinPayoutProgress },
  streak: { useGet: useStreak },
  mtdProgress: { useGet: useMtdProgress },
  cohortComparison: { useGet: useCohortComparison },
  autoPayout: { useUpdate: useUpdateAutoPayout },
};
